import { createStyles, lighten, makeStyles, Theme } from '@material-ui/core';
import { DatePicker } from '@talentnow/shared-ui/form-components';
import moment from 'moment';
import React, { useEffect, useMemo } from 'react';

import TextField from '../../../../_components/forms/TextField';
import TnButton from '../../../../_components/tn-button/TnButton';
import TnCheckbox from '../../../../_components/tn-checkbox/TnCheckbox';
import TnTextEditor from '../../../../_components/tn-text-editor/TnTextEditor';
import TnTypography from '../../../../_components/tn-typography/TnTypography';

const REQUIRED_FIELD_MESSAGE = 'This field is required';
const END_DATE_BEFORE_START_MESSAGE = 'End date cannot be before start date';

const FIELD_LABELS: Record<string, string> = {
  companyName: 'Company Name',
  jobTitle: 'Job Title',
  startDate: 'Experience Start Date',
  endDate: 'Experience End Date',
};

export interface Employment {
  id?: string;
  companyName: string;
  jobTitle: string;
  startDate: string;
  endDate: string;
  additionalInfo: string;
}

interface Props {
  mode: 'create' | 'edit';
  onCancel: () => void;
  onDelete?: () => void;
  onSubmit: (employment: Employment) => void;
  employment?: Employment;
  onValidityChange?: (isValid: boolean, invalidFields: string[]) => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    bodyContainer: {
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 12,
    },
    bodyElement: {
      width: 'calc(50% - 8px)',
    },
    fullWidthElement: {
      width: '100%',
    },
    additionalInfoContainer: {
      marginTop: -16,
      width: '100%',
    },
    buttonRow: {
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
      marginBottom: 16,
    },
    createModeButtonContainer: {
      display: 'flex',
      gap: 8,
      flexDirection: 'row',
    },
    deleteButton: {
      backgroundColor: '#B00020',
      color: 'white',
      padding: '10px 20px',
      border: 'solid',
      borderRadius: '6px',
      '&:hover': {
        backgroundColor: lighten('#B00020', 0.1),
      },
    },
  }),
);

export default function EmploymentForm({
  mode,
  onSubmit,
  onCancel,
  onDelete,
  employment,
  onValidityChange,
}: Readonly<Props>) {
  const classes = useStyles();

  const [companyName, setCompanyName] = React.useState('');
  const [jobTitle, setJobTitle] = React.useState('');
  const [startDate, setStartDate] = React.useState('');
  const [endDate, setEndDate] = React.useState('');
  const [endDateDisabled, setEndDateDisabled] = React.useState(false);
  const [additionalInfo, setAdditionalInfo] = React.useState('');
  const [isCurrentPosition, setIsCurrentPosition] = React.useState(false);
  const [touchedFields, setTouchedFields] = React.useState<Record<string, boolean>>({});

  useEffect(() => {
    if (employment && mode === 'edit') {
      setCompanyName(employment.companyName);
      setJobTitle(employment.jobTitle);
      setStartDate(employment.startDate);
      setEndDate(employment.endDate);
      setAdditionalInfo(employment.additionalInfo);
      setTouchedFields({ companyName: true, jobTitle: true, startDate: true, endDate: true });
      if (!employment.endDate) {
        setIsCurrentPosition(true);
        setEndDateDisabled(true);
      }
    }
  }, [employment, mode]);

  const parsedStartDate = useMemo(() => moment(startDate), [startDate]);
  const parsedEndDate = useMemo(() => moment(endDate), [endDate]);

  const markFieldTouched = (field: string) => setTouchedFields((previous) => ({ ...previous, [field]: true }));

  const errors = useMemo(() => {
    const fieldErrors: Record<string, string> = {};

    if (!companyName) {
      fieldErrors.companyName = REQUIRED_FIELD_MESSAGE;
    }

    if (!jobTitle) {
      fieldErrors.jobTitle = REQUIRED_FIELD_MESSAGE;
    }

    if (!parsedStartDate.isValid()) {
      fieldErrors.startDate = REQUIRED_FIELD_MESSAGE;
    }

    if (isCurrentPosition) {
      return fieldErrors;
    }

    if (!parsedEndDate.isValid()) {
      fieldErrors.endDate = REQUIRED_FIELD_MESSAGE;
    } else if (parsedStartDate.isValid() && parsedEndDate.isBefore(parsedStartDate)) {
      fieldErrors.endDate = END_DATE_BEFORE_START_MESSAGE;
    }

    return fieldErrors;
  }, [companyName, jobTitle, parsedStartDate, parsedEndDate, isCurrentPosition]);

  const isFormValid = useMemo(() => Object.keys(errors).length === 0, [errors]);
  const invalidFields = useMemo(() => Object.keys(errors).map((field) => FIELD_LABELS[field]), [errors]);

  useEffect(() => {
    onValidityChange?.(isFormValid, invalidFields);
  }, [isFormValid, invalidFields]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={classes.bodyContainer}>
      <div className={classes.bodyElement}>
        <TextField
          required
          name="companyName"
          label="Company Name"
          value={companyName}
          error={errors.companyName}
          touched={touchedFields.companyName}
          onChange={(value) => setCompanyName(value)}
          onBlur={() => markFieldTouched('companyName')}
        />
      </div>
      <div className={classes.bodyElement}>
        <TextField
          required
          name="jobTitle"
          label="Job Title"
          value={jobTitle}
          error={errors.jobTitle}
          touched={touchedFields.jobTitle}
          onChange={(value) => setJobTitle(value)}
          onBlur={() => markFieldTouched('jobTitle')}
        />
      </div>
      <div className={classes.fullWidthElement} style={{ marginLeft: 4, marginTop: -16 }}>
        <TnCheckbox
          label="Current Job"
          value={isCurrentPosition}
          onChange={(checked) => {
            setIsCurrentPosition(checked);
            setEndDateDisabled(checked);
            setEndDate(checked ? '' : endDate);
          }}
        />
      </div>
      <div className={classes.bodyElement}>
        <DatePicker
          required
          monthYearOnly
          name="startDate"
          label="Start Date"
          placeholder="MM/YYYY"
          value={startDate}
          minDate={'01/01/1900'}
          error={errors.startDate}
          touched={!!touchedFields.startDate}
          onChange={(value) => {
            markFieldTouched('startDate');
            setStartDate(value);
          }}
          onBlur={() => markFieldTouched('startDate')}
        />
      </div>
      <div className={classes.bodyElement}>
        <DatePicker
          required={!isCurrentPosition}
          monthYearOnly
          name="endDate"
          label="End Date"
          placeholder="MM/YYYY"
          value={endDate}
          disabled={endDateDisabled}
          minDate={'01/01/1900'}
          error={errors.endDate}
          touched={!!touchedFields.endDate}
          onChange={(value) => {
            markFieldTouched('endDate');
            setEndDate(value);
          }}
          onBlur={() => markFieldTouched('endDate')}
        />
      </div>
      <div className={classes.additionalInfoContainer}>
        <TnTypography variant="body2">Additional Information</TnTypography>
        <TnTextEditor value={additionalInfo} onTextChange={(value) => setAdditionalInfo(value)} />
      </div>
      <div className={classes.buttonRow}>
        <div>
          {mode === 'edit' ? (
            <TnButton
              variant="contained"
              color="secondary"
              classes={{ root: classes.deleteButton }}
              value="Delete"
              onClick={() => onDelete?.()}
            />
          ) : null}
        </div>
        <div className={classes.createModeButtonContainer}>
          <TnButton variant="outlined" color="primary" value="Cancel" onClick={() => onCancel()} />
          <TnButton
            variant="contained"
            color="primary"
            value={mode === 'create' ? 'Add Experience' : 'Save Changes'}
            disabled={!isFormValid}
            onClick={() => {
              onSubmit({
                id: employment?.id,
                companyName,
                jobTitle,
                startDate,
                endDate: isCurrentPosition ? '' : endDate,
                additionalInfo,
              });
            }}
          />
        </div>
      </div>
    </div>
  );
}
